import React, { useEffect, useMemo, useState } from 'react';
import {
  CASE_ORDER,
  evaluateStress,
  fmtInt,
  fmtMM,
  fmtMonth,
  fmtNumMM,
  fmtPct,
  fmtX,
  packageFacts,
  PRICE_BANDS,
  stressCaseCopy,
  valueCases,
  valueAssistantSummary,
} from './model.js';
import { IRR_NOMINAL_LABEL, effectiveAnnualFromNominal } from '../engine/dealEngine.js';
import { Loading, NextStep, Src, useAssistantSummary, useMeeting } from './common.jsx';
import { warmAbsScreen } from './absCache.js';

const finite = (v) => typeof v === 'number' && Number.isFinite(v);

function NumberField({ label, value, onCommit, width = 96, step = '0.1', suffix }) {
  const [text, setText] = useState(String(value ?? ''));
  useEffect(() => {
    setText(String(value ?? ''));
  }, [value]);
  const commit = () => {
    const n = Number(String(text).replace(/[$,\s]/g, ''));
    // Only commit real edits: a focus/blur on an untouched field must not pin a
    // desk override (that would hide a later D2D estimate).
    if (String(text).trim() === String(value ?? '').trim()) return;
    if (Number.isFinite(n) && n >= 0 && String(text).trim() !== '') onCommit(n);
    else setText(String(value ?? ''));
  };
  return (
    <label className="mp-inline-field">
      {label}
      <input
        className="mp-input"
        style={{ width }}
        inputMode="decimal"
        step={step}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
        }}
      />
      {suffix ? <span>{suffix}</span> : null}
    </label>
  );
}

export default function Value({ dealId, dealPackage, dealPackageFailed, reserves, executive, onNavigate }) {
  const { desk, patchDesk, forecast } = useMeeting({ dealId, dealPackage });
  const facts = useMemo(
    () => (dealPackage ? packageFacts(dealPackage, { executive, reserves }) : null),
    [dealPackage, executive, reserves],
  );
  const rows = useMemo(
    () => (dealPackage ? valueCases(dealPackage, forecast, desk) : []),
    [dealPackage, forecast, desk],
  );
  const stress = useMemo(
    () => (dealPackage ? evaluateStress(dealPackage, desk) : null),
    [dealPackage, desk],
  );
  const byId = Object.fromEntries(rows.map((r) => [r.id, r]));
  const base = byId.base || byId.seller;

  // Warm the ABS engine so the Finance step opens instantly in a meeting.
  useEffect(() => {
    if (dealPackage) warmAbsScreen(dealId, desk);
  }, [dealPackage, dealId, desk]);

  // Exact on-screen case numbers (incl. MOIC + NTM CF) so the assistant quotes, never re-derives.
  useAssistantSummary(rows.length ? valueAssistantSummary(rows, desk, { stressIrr: stress?.irrAnnual ?? null }) : null);

  if (!dealPackage) {
    return dealPackageFailed ? (
      <div className="mp">
        <div className="mp-empty">No deal package is loaded for this deal on this host yet.</div>
      </div>
    ) : (
      <Loading label="Valuing the cases…" />
    );
  }

  const bid = desk.bidPriceMM;
  const pv = facts?.pv || {};
  const pvMax = Math.max(pv[10] || 0, bid || 0);
  const barW = (v) => (finite(v) && pvMax > 0 ? `${Math.max(2, (v / pvMax) * 100).toFixed(1)}%` : '0%');
  // Oil-only packages carry WTI in the hh knob; multi-product oil desks keep a true
  // Henry Hub in hh and WTI in wti (Sedgwick). Gas desks quote HH.
  const primary = dealPackage.meta?.primaryProduct || dealPackage.assumptions?.primaryProduct;
  const commodity = dealPackage.meta?.commodity || primary;
  const hhKnob = Number(dealPackage.assumptions?.hh);
  const wtiKnob = Number(dealPackage.assumptions?.wti);
  const hhLabel = !Number.isFinite(hhKnob)
    ? 'strip'
    : commodity === 'oil'
      ? `WTI $${hhKnob.toFixed(2)}`
      : primary === 'oil' && Number.isFinite(wtiKnob)
        ? `WTI $${wtiKnob.toFixed(2)} · HH $${hhKnob.toFixed(2)}`
        : `HH $${hhKnob.toFixed(2)}`;
  const oilOnly = primary === 'oil' && !/gas|ngl/i.test(String(dealPackage.meta?.commodity || 'oil'));
  const realizedUnit = facts?.l12RealizedUnit || (primary === 'oil' ? '$/bbl' : '$/Mcf');
  const loeUnit = facts?.l12LoeUnit || realizedUnit;
  const taxRate = dealPackage.meta?.taxRate;
  const ordered = CASE_ORDER.filter((id) => byId[id]);
  const pv15 = pv[15];
  const pv17 = pv[17];
  const buyerOh = base?.result?.buyerOverhead;

  return (
    <div className="mp">
      <div className="mp-head">
        <div>
          <span className="mp-eyebrow">
            Value · {facts?.priceDeck || hhLabel} · 10% a year, applied monthly{desk.aroMM > 0 ? ' · ARO deducted' : ''}
          </span>
          <h2 className="mp-h2">What it's worth under each view, and what we'd pay</h2>
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <NumberField label="Our bid ($MM)" value={bid} onCommit={(n) => patchDesk({ bidPriceMM: n })} />
            {desk.bidDefaultLabel ? (
              <span className="mp-note" data-testid="bid-default-label">{desk.bidDefaultLabel}</span>
            ) : null}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <NumberField label="ARO ($MM)" value={desk.aroSet ? +Number(desk.aroMM || 0).toFixed(2) : ''} onCommit={(n) => patchDesk({ aroMM: n })} width={84} />
            <span className="mp-note" style={{ textAlign: 'right' }}>
              {desk.aroSource === 'desk' ? (
                <button type="button" className="mp-link" style={{ fontSize: 12, fontWeight: 500 }} onClick={() => patchDesk({ aroMM: null })}>
                  desk override · reset
                </button>
              ) : desk.aroSource ? (
                `from ${desk.aroSource}`
              ) : (
                'not set'
              )}
            </span>
          </div>
        </div>
      </div>

      <section className="mp-card" aria-label="Cases side by side">
          <h3 className="mp-h3">
            {ordered.length} views of the same package, $MM · bid {fmtMM(bid, 1)}
          </h3>
          <div className="mp-table-wrap">
            <table className="mp-table" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  <th />
                  {ordered.map((id) => (
                    <th key={id} className={`num ${id === 'base' ? 'hl' : ''}`} style={{ textTransform: 'none', letterSpacing: 0 }}>
                      <div style={{ fontSize: 14, color: 'var(--mp-ink)', fontWeight: 700 }}>{byId[id].kase.label}</div>
                      <div style={{ fontWeight: 400, fontSize: 12 }}>{byId[id].kase.sub}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ whiteSpace: 'nowrap' }}>PDP PV10</td>
                  {ordered.map((id) => (
                    <td key={id} className={`num ${id === 'base' ? 'hl' : ''}`} style={id === 'base' ? { fontWeight: 600 } : undefined}>
                      {fmtNumMM(byId[id].pv10MM)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td style={{ whiteSpace: 'nowrap' }}>After ARO</td>
                  {ordered.map((id) => (
                    <td key={id} className={`num ${id === 'base' ? 'hl' : ''}`}>
                      {fmtNumMM(byId[id].afterAroMM)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td style={{ whiteSpace: 'nowrap' }}>Next-12-mo cash flow</td>
                  {ordered.map((id) => (
                    <td key={id} className={`num ${id === 'base' ? 'hl' : ''}`}>
                      {fmtNumMM(byId[id].ntmCfMM)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <b title="Effective annual rate is (1 + nominal/12)^12 − 1">{IRR_NOMINAL_LABEL}</b>
                  </td>
                  {ordered.map((id) => {
                    const irr = byId[id].irr;
                    const eff = byId[id].irrEffective ?? effectiveAnnualFromNominal(irr);
                    return (
                      <td
                        key={id}
                        className={`num ${id === 'base' ? 'hl' : ''} ${irr != null && irr < 0.1 ? 'bad' : ''}`}
                        style={{ fontWeight: 700 }}
                        title={eff == null ? IRR_NOMINAL_LABEL : `${IRR_NOMINAL_LABEL}. Effective annual ${fmtPct(eff, 2)}.`}
                      >
                        {irr == null ? 'n/m' : fmtPct(irr)}
                        {eff == null ? null : <div className="mp-note">effective {fmtPct(eff, 2)}</div>}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td style={{ whiteSpace: 'nowrap' }}>MOIC (before P&amp;A)</td>
                  {ordered.map((id) => (
                    <td key={id} className={`num ${id === 'base' ? 'hl' : ''}`}>
                      {fmtX(byId[id].moic)}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td style={{ whiteSpace: 'nowrap' }}>Bid ÷ PV10</td>
                  {ordered.map((id) => (
                    <td key={id} className={`num ${id === 'base' ? 'hl' : ''}`}>
                      {fmtPct(byId[id].bidPctOfPv, 0)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <span className="mp-note">
              Cases differ only in the forecast for the largest well (set on Forecast). IRR below 10% shown in red.
            </span>
            <button type="button" className="mp-link" onClick={() => onNavigate('decision')}>
              Price &amp; netback scenarios →
            </button>
          </div>
        </section>


      <div className="mp-row">
        <section className="mp-card dark mp-grow-2" aria-label="At our bid">
          <div style={{ fontSize: 13, letterSpacing: '0.06em', fontWeight: 600, color: 'var(--mp-amber)' }}>
            AT A {fmtMM(bid, 1).toUpperCase()} BID · OUR BASE
          </div>
          <div className="mp-bigstats">
            <div title={base?.irrEffective == null ? IRR_NOMINAL_LABEL : `${IRR_NOMINAL_LABEL}. Effective annual ${fmtPct(base.irrEffective, 2)}.`}>
              <div className={`v ${base?.irr != null && base.irr < 0.1 ? 'bad' : ''}`}>
                {base?.irr == null ? 'n/m' : fmtPct(base.irr)}
              </div>
              <div className="k">{IRR_NOMINAL_LABEL}</div>
              {base?.irrEffective != null ? <div className="mp-note">effective {fmtPct(base.irrEffective, 2)}</div> : null}
            </div>
            <div>
              <div className="v">{fmtX(base?.moic)}</div>
              <div className="k">MOIC (before P&amp;A)</div>
            </div>
            <div>
              <div className="v">{finite(pv15) && pv15 > 0 ? fmtX(bid / pv15) : '—'}</div>
              <div className="k">of seller PV-15 ({fmtMM(pv15, 1)})</div>
            </div>
            <div title={stress?.irrEffective == null ? IRR_NOMINAL_LABEL : `${IRR_NOMINAL_LABEL}. Effective annual ${fmtPct(stress.irrEffective, 2)}.`}>
              <div className={`v ${stress?.irrAnnual == null || stress.irrAnnual < 0.05 ? 'bad' : ''}`}>
                {stress?.irrAnnual == null ? 'n/m' : fmtPct(stress.irrAnnual)}
              </div>
              <div className="k">{IRR_NOMINAL_LABEL}, stress case</div>
              {stress?.irrEffective != null ? <div className="mp-note">effective {fmtPct(stress.irrEffective, 2)}</div> : null}
            </div>
          </div>
          <p className="mp-p" style={{ color: '#D4DEE5', borderTop: '1px solid var(--mp-navy-3)', paddingTop: 14 }}>
            {finite(pv17) ? (
              <>
                <b style={{ color: '#fff' }}>Judgment:</b> ABS-backed buyers we&apos;ve talked to price PDP at about{' '}
                <b style={{ color: '#fff' }}>PV-17 ≈ {fmtMM(pv17, 1)}</b> here.{' '}
                {bid > pv17
                  ? `A ${fmtMM(bid, 1)} bid is above that, so more of the purchase falls on equity.`
                  : `A ${fmtMM(bid, 1)} bid is at or below that yardstick.`}
              </>
            ) : (
              'No PV-sensitivity table in the reserve report, so PV-15/17 yardsticks are unavailable.'
            )}{' '}
            {stressCaseCopy(dealPackage)} {oilOnly ? PRICE_BANDS.valueOil + '. Our base equals Seller.' : ''}
          </p>
        </section>
        <section className="mp-card mp-grow-3" aria-label="How the market would quote it">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
          <h3 className="mp-h3">How the market would quote it</h3>
          <span className="mp-note">Seller reserve report, PDP, $MM · PV-17 interpolated · Judgment</span>
        </div>
        <div className="mp-bars">
          {[10, 15, 17, 20].map((r) => (
            <React.Fragment key={r}>
              <span style={{ fontWeight: 600 }}>PV-{r}</span>
              <div className={`mp-bar ${r === 17 ? 'pv17' : ''}`} style={{ width: barW(pv[r]) }} />
              <span className="mp-num" style={{ textAlign: 'right' }}>
                {r === 17 && finite(pv[r]) ? '≈' : ''}
                {fmtNumMM(pv[r], 1)}
              </span>
            </React.Fragment>
          ))}
          <span style={{ fontWeight: 600, color: '#9A5B05' }}>Our bid</span>
          <div className="mp-bar bid" style={{ width: barW(bid) }} />
          <span className="mp-num" style={{ textAlign: 'right', fontWeight: 700 }}>
            {fmtNumMM(bid, 1)}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', fontSize: 14, color: 'var(--mp-ink-2)', borderTop: '1px solid var(--mp-line-2)', paddingTop: 12 }}>
          <span>
            Price per flowing {primary === 'oil' ? 'bbl/d' : 'Mcf/d'}:{' '}
            <b className="mp-num">
              {finite(facts?.flowingRate) && facts.flowingRate > 0
                ? `$${fmtInt((bid * 1e6) / facts.flowingRate)}`
                : '—'}
            </b>
            {finite(facts?.flowingRate) ? <span className="mp-note"> (at {fmtInt(facts.flowingRate)} {primary === 'oil' ? 'bbl/d' : 'Mcf/d'})</span> : null}
          </span>
          <span>
            5× next-12-month cash flow: <b className="mp-num">{fmtMM(base?.result?.multiples?.x5, 1)}</b>
          </span>
          <span>
            PV-weighted reserve life:{' '}
            <b className="mp-num">{finite(facts?.pvWeightedLife) ? `${facts.pvWeightedLife.toFixed(1)} yrs` : '—'}</b>
          </span>
        </div>
      </section>
      </div>

      <div className="mp-row">
        <section className="mp-card mp-grow-1" aria-label="Costs and sources">
          <h3 className="mp-h3">Costs: where each number comes from</h3>
          <div>
            <div className="mp-source-row">
              <Src kind="seller" />
              <span>
                <b>Lease operating expense</b>: {finite(facts?.l12LoePerUnit) ? `$${facts.l12LoePerUnit.toFixed(2)} ${loeUnit}` : '—'}
                {finite(facts?.l12LoeMonthly) ? ` (~$${fmtInt(facts.l12LoeMonthly / 1000)}K a month)` : ''}, from the seller's net LOS
                {facts?.losRange ? ` ${fmtMonth(facts.losRange[0])}–${fmtMonth(facts.losRange[1])}` : ''}.
              </span>
            </div>
            <div className="mp-source-row">
              <Src kind="seller" />
              <span>
                <b>Netback</b>: realized {finite(facts?.l12RealizedPrice) ? `$${facts.l12RealizedPrice.toFixed(2)} ${realizedUnit}` : '—'} over the
                last 12 months vs. {hhLabel} strip.
              </span>
            </div>
            <div className="mp-source-row">
              <Src kind="model" />
              <span>
                <b>Production taxes (blended)</b>
                {finite(taxRate) ? `: ${(taxRate * 100).toFixed(1)}% of revenue on the desk.` : ' on the desk.'}{' '}
                Collateral PV-10 uses severance and ad valorem when those rates are known.
              </span>
            </div>
            <div className="mp-source-row">
              <Src kind="judgment" />
              <span>
                <b>ARO {desk.aroSet ? fmtMM(desk.aroMM, 2) : 'not set'}</b>
                {desk.aroSet && desk.aroSource ? ` (${desk.aroSource === 'desk' ? 'desk override' : desk.aroSource})` : ''}
                {facts?.negativeWells ? `: ${facts.negativeWells} negative-PV wells carry the plug liability.` : '.'}{' '}
                {desk.aroSet ? '' : 'Enter a P&A estimate above. Finance needs it to size the note.'}
              </span>
            </div>
            <div className="mp-source-row">
              <Src kind="judgment" />
              <span>
                <b>Buyer G&amp;A ~${fmtInt((buyerOh?.monthlyTotal || 0) / 1000)}K/mo (illustrative)</b>:{' '}
                {buyerOh?.monthlyIncluded ? 'included in cash flow.' : <i>not</i>}
                {buyerOh?.monthlyIncluded ? '' : ' in cash flow yet. The stress case turns it on.'}
              </span>
            </div>
          </div>
        </section>
        <div className="mp-grow-1">
          <NextStep
            label="4 · Finance"
            question={`How much of a ${fmtMM(bid, 1)} purchase can debt carry?`}
            onClick={() => onNavigate('finance')}
          />
        </div>
      </div>
    </div>
  );
}
